///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormsModule } from '@angular/forms';
import { PARAM_BBOX } from '../../../shared/constants/url-params.constants';
import { ProjectInteractiveWindowsPopupService } from '../project-interactive-windows-popup.service';
import { ActivatedRoute } from '@angular/router';
import { AbstractInteractiveWindow } from '../abstract-interactive-window';
import { Subject } from 'rxjs';
import { GtthManualDesignStepsEnum } from './manual-design-steps.enum';
import { GtthHistoryService } from '../../logical-topology/engine/history-service';
import { GtthLogicalTopology } from '../../logical-topology/entities/logical-topology';
import { ProjectDesignConfigDTO } from '../../../generated/shared-types.model';
import { GtthGetDesignConfigCommand } from '../../logical-topology/engine/commands/design/manual-design/get-design-config-command';
import { GtthClearDesignCommand } from '../../logical-topology/engine/commands/design/manual-design/clear-design-command';
import { GtthAutoSelectProductsCommand } from '../../logical-topology/engine/commands/design/manual-design/auto-select-products-command';
import { GtthDesignPtMPCommand } from '../../logical-topology/engine/commands/design/manual-design/design-ptmp-command';
import { GtthDesignRingsCommand } from '../../logical-topology/engine/commands/design/manual-design/design-rings-command';
import { GtthAutoSetFrequenciesCommand } from '../../logical-topology/engine/commands/design/manual-design/auto-set-frequencies-command';
import { GtthAutoConfigureRingsCommand } from '../../logical-topology/engine/commands/design/manual-design/auto-configure-rings-command';
import { GtthNotificationsService } from '../../logical-topology/engine/notifications-service';
import { GtthLayerDtoModel } from '../../../services/project-socket/dto/gtth-layer-dto.model';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { GtthReactiveNumber } from '../../../shared/reactive-forms/input-number';
import { GtthReactiveCheckbox } from '../../../shared/reactive-forms/input-checkbox';
import { GtthInputControlService } from '../../../shared/reactive-forms/input-control.service';
import { parseFormDataToObj } from '../../../shared/utils';
import {
    initReactiveControlsForLayer,
    updateReactiveFormForLayer,
    updateTunnelAndRedundancyFactorFields
} from '../design-dialogs-shared-utils';
import { GtthReactiveDropdown } from '../../../shared/reactive-forms/input-dropdown';
import { GtthRefreshTopologyCommand } from '../../logical-topology/engine/commands/project/refresh-topology-command';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { SuiSvgIconComponent, SuiTranslatePipe } from '@siklu/sui-lib';
import { TabsNavComponent } from '../../../shared/tabs-nav/tabs-nav.component';
import { GtthSharedCommonModule } from '../../../shared/shared-common.module';
import { GtthNumberInputValidationDirective } from '../../../shared/directives/validation-directives/number-input.directive';

@Component({
    selector: 'winde-manual-design',
    templateUrl: './manual-design.component.html',
    styleUrls: ['./manual-design.scss', '../project-windows.scss'],
    imports: [
        FormsModule,
        DragDropModule,
        MatDialogModule,
        SuiTranslatePipe,
        SuiSvgIconComponent,
        TabsNavComponent,
        GtthSharedCommonModule,
        GtthNumberInputValidationDirective
    ]
})
export class GtthManualDesignComponent extends AbstractInteractiveWindow implements OnInit, OnDestroy {
    public redundancyEnabled: boolean[] = [];
    public resiliencyEnabled: boolean[] = [];
    public redundancyPercent: number[] = [];
    public nodesCountPerLayer: number[] = [];
    public layerContainsMhTgBu: boolean[] = [];
    public showFormValidationAlert: boolean;

    public totalNumberOfNodes: number;
    public alertMessageSubject: Subject<string>;
    public manualDesignSteps = GtthManualDesignStepsEnum;
    public readonly tabsToShow = [
        GtthManualDesignStepsEnum[GtthManualDesignStepsEnum.DESIGN_P2MP],
        GtthManualDesignStepsEnum[GtthManualDesignStepsEnum.DESIGN_RINGS],
        GtthManualDesignStepsEnum[GtthManualDesignStepsEnum.AUTO_SET_FREQUENCIES]
    ];
    public currentTab = GtthManualDesignStepsEnum.DESIGN_P2MP;
    public p2mpSelectedLayer: number = GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX;
    public copyP2mpConfigFromLayer: number = GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX;
    public ringsSelectedLayer: number = GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX;
    public copyRingsConfigFromLayer: number = GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX;
    public frequenciesDefaultLayer: number = GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX;
    public layersMaskObject = {};
    public designConfig: ProjectDesignConfigDTO;

    public p2mpDesignDirection: boolean[] = [];
    public ringsDesignDirection: boolean[] = [];

    public autodesignP2mpLayersPerLayer: number[] = [];
    public autodesignP2mpLayersPerLayerString: string[][] = [];
    public designRingsLayersPerLayer: number[] = [];
    public designRingsLayersPerLayerString: string[][] = [];

    public hasNodesMappedToLayer: boolean[];
    public isCommandInProgress = false;

    public editForm: FormGroup;
    public capFacPerLayer: GtthReactiveNumber[] = [];
    public costFacPerLayer: GtthReactiveNumber[] = [];
    public lcentDistPerLayer: GtthReactiveNumber[] = [];
    public limBoxesPerLayer: GtthReactiveCheckbox[] = [];
    public nCandWinPerLayer: GtthReactiveNumber[] = [];
    public optBsFacPerLayer: GtthReactiveNumber[] = [];
    public peerBsFacPerLayer: GtthReactiveNumber[] = [];
    public p2pDistFacPerLayer: GtthReactiveNumber[] = [];
    public redunFacPerLayer: GtthReactiveNumber[] = [];
    public takeRatePerLayer: GtthReactiveNumber[] = [];
    public tgMaxIttrPerLayer: GtthReactiveNumber[] = [];
    public tunnelFacPerLayer: GtthReactiveNumber[] = [];
    public asMethodPerLayer: GtthReactiveDropdown[] = [];
    public maxDevNumPerLayer: GtthReactiveDropdown[] = [];
    public popDistFacPerLayer: GtthReactiveNumber[] = [];

    constructor(
        private readonly elementRef: ElementRef,
        public dialogRef: MatDialogRef<GtthManualDesignComponent>,
        public historyService: GtthHistoryService,
        public notificationService: GtthNotificationsService,
        private readonly inputControlService: GtthInputControlService
    ) {
        super(elementRef);
        this.totalNumberOfNodes = this.historyService.logicalTopology.getTerminalStationsCount();
        this.alertMessageSubject = new Subject();
    }

    ngOnInit() {
        if (!this.historyService.isEditModeEnabled) {
            this.clear();
            return;
        }

        this.createLayersMask();
        this.refreshDesignState();
        this.refreshDataPerLayer();

        if (this.historyService.logicalTopology.layersModeEnabled) {
            this.historyService.logicalTopology.layers.forEach((layer: GtthLayerDtoModel, layerIdx) => {
                this.nodesCountPerLayer[layerIdx] = this.getNodesPerLayerCount(layerIdx);
                this.layerContainsMhTgBu[layerIdx] = this.historyService.logicalTopology.selectedProductsContainsMhTgBu(
                    this.historyService.projectProductsService,
                    layerIdx
                );
                this.initReactiveControlsForLayer(layerIdx, this.layerContainsMhTgBu[layerIdx]);
                this.resiliencyEnabled[layer.layerIndex] =
                    this.historyService.logicalTopology.autodesignP2mpPerLayer[layer.layerIndex].isResiliencyEnabled();
                this.setRedundancyEnabled(layerIdx, this.historyService.logicalTopology.designRingsPerLayer[layer.layerIndex].rsz_th > 0);
            });
            this.initForm(this.p2mpSelectedLayer);
        } else {
            this.layerContainsMhTgBu[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX] =
                this.historyService.logicalTopology.selectedProductsContainsMhTgBu(
                    this.historyService.projectProductsService,
                    GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX
                );
            this.initReactiveControlsForLayer(
                GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX,
                this.layerContainsMhTgBu[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX]
            );
            this.initForm(GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX);
            this.nodesCountPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX] = this.getNodesPerLayerCount(
                GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX
            );

            this.resiliencyEnabled[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX] =
                this.historyService.logicalTopology.autodesignP2mpPerLayer[
                    GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX
                ].isResiliencyEnabled();

            this.setRedundancyEnabled(
                GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX,
                this.historyService.logicalTopology.designRingsPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX].rsz_th > 0
            );
        }
    }

    ngOnDestroy() {
        if (this.alertMessageSubject) {
            this.alertMessageSubject.complete();
        }
    }

    setRedundancyEnabled(layerIndex: number, newValue: boolean): void {
        this.redundancyEnabled[layerIndex] = newValue;

        if (!newValue) {
            // clear redundancy data
            this.clearRedundancy(layerIndex);
        } else {
            const ringLayer = this.historyService.logicalTopology.designRingsPerLayer[layerIndex];

            if (!ringLayer.rsz_th) {
                ringLayer.rsz_th = this.nodesCountPerLayer[layerIndex] > 10 ? 10 : this.nodesCountPerLayer[layerIndex];
            }

            this.calculateRedundancy(layerIndex, this.historyService.logicalTopology.designRingsPerLayer[layerIndex].rsz_th);
            this.historyService.logicalTopology.designRingsPerLayer[layerIndex].rsz_weig = 1;
        }
    }

    onWorstCaseChange(wc_analysis: boolean): void {
        this.historyService.logicalTopology.autoSetFrequenciesPerLayer[this.frequenciesDefaultLayer].explicit_only = !wc_analysis;
    }

    onResiliencyEnabledChange(isResiliencyEnabled: boolean, layerIndex: number): void {
        this.resiliencyEnabled[layerIndex] = isResiliencyEnabled;
        updateTunnelAndRedundancyFactorFields(this, layerIndex);
    }

    calculateRedundancy(activeLayerTab: number, value: number, isCalculateThreshold = false): void {
        const nodesCount = this.nodesCountPerLayer[activeLayerTab];

        if (isCalculateThreshold) {
            this.historyService.logicalTopology.designRingsPerLayer[activeLayerTab].rsz_th = Math.round((value * nodesCount) / 100);
            return;
        }

        const res = (value / nodesCount) * 100;
        this.redundancyPercent[activeLayerTab] = Math.round(res * 10) / 10;
    }

    copyDesignSettingsFromLayer(currentTab: number) {
        if (currentTab === this.manualDesignSteps.DESIGN_P2MP) {
            //    copy autodesignP2mpPerLayer settings from selected layer to current
            Object.keys(this.historyService.logicalTopology.autodesignP2mpPerLayer[this.p2mpSelectedLayer]).forEach((key: string) => {
                if (key !== 'feed_by') {
                    this.historyService.logicalTopology.autodesignP2mpPerLayer[this.p2mpSelectedLayer][key] =
                        this.historyService.logicalTopology.autodesignP2mpPerLayer[this.copyP2mpConfigFromLayer][key];
                }
            });
            this.updateReactiveFormForLayer(this.p2mpSelectedLayer);
            this.refreshDataPerLayer();
        }
        if (currentTab === this.manualDesignSteps.DESIGN_RINGS) {
            //    copy designRingsPerLayer settings from selected layer to current
            Object.keys(this.historyService.logicalTopology.designRingsPerLayer[this.ringsSelectedLayer]).forEach((key: string) => {
                if (key !== 'supp_layer') {
                    this.historyService.logicalTopology.designRingsPerLayer[this.ringsSelectedLayer][key] =
                        this.historyService.logicalTopology.designRingsPerLayer[this.copyRingsConfigFromLayer][key];
                }
            });
            this.refreshDataPerLayer();
        }
    }

    changeTab(tabIndex: string) {
        this.currentTab = GtthManualDesignStepsEnum[tabIndex];
        if (this.currentTab === GtthManualDesignStepsEnum.DESIGN_P2MP) {
            this.initReactiveControlsForLayer(this.p2mpSelectedLayer, this.layerContainsMhTgBu[this.p2mpSelectedLayer]);
            this.initForm(this.p2mpSelectedLayer);
        }
        this.resiliencyEnabled[this.p2mpSelectedLayer] =
            this.historyService.logicalTopology.autodesignP2mpPerLayer[this.p2mpSelectedLayer].isResiliencyEnabled();
    }

    changeDesignDirection(tab: string, layer: number, value) {
        this.historyService.logicalTopology[tab][layer].bottom_up = value[layer];
    }

    saveP2MpConnectedLayersToTopology(selectedArray: string[], layer: number) {
        this.autodesignP2mpLayersPerLayerString[layer] = [...selectedArray];
        if (selectedArray.length) {
            let finalValue = 0;
            selectedArray.forEach((item) => {
                if (this.layersMaskObject[item]) {
                    finalValue = finalValue | this.layersMaskObject[item];
                }
            });
            this.autodesignP2mpLayersPerLayer[layer] = finalValue;
        } else {
            this.autodesignP2mpLayersPerLayer[layer] = null;
        }
        this.saveP2mpLayersToTopology(layer);
    }

    saveRingsConnectedLayersToTopology(selectedArray: string[], layer: number) {
        this.designRingsLayersPerLayerString[layer] = [...selectedArray];
        if (selectedArray.length) {
            let finalValue = 0;
            selectedArray.forEach((item) => {
                if (this.layersMaskObject[item]) {
                    finalValue = finalValue | this.layersMaskObject[item];
                }
            });
            this.designRingsLayersPerLayer[layer] = finalValue;
        } else {
            this.designRingsLayersPerLayer[layer] = null;
        }
        this.saveDesignRingsLayersToTopology(layer);
    }

    getLayersNamesFromBytes(tabLayersArrPerLayer: number): string[] {
        if (tabLayersArrPerLayer) {
            const res = [];
            let val;
            Object.keys(this.layersMaskObject).forEach((key) => {
                val = this.layersMaskObject[key] | tabLayersArrPerLayer;
                if (val === tabLayersArrPerLayer) {
                    res.push(key);
                }
            });
            return res;
        } else {
            return [];
        }
    }

    clear() {
        if (this.editForm.invalid) {
            return;
        }
        parseFormDataToObj(this.historyService.logicalTopology.autodesignP2mpPerLayer[this.p2mpSelectedLayer], this.editForm);
        this.dialogRef.close('cancel');
    }

    clearDesign(): void {
        this.isCommandInProgress = true;
        const command = this.historyService.command(GtthClearDesignCommand);
        this.historyService.doCommandBypassHistory(command).then((result) => {
            this.isCommandInProgress = false;
            if (result) {
                this.notificationService.showSuccess('Design cleared successfully', 5000);
                this.refreshDesignState();
            } else {
                this.notificationService.showError('Design not cleared', 5000);
            }
        });
    }

    async autoSelectProducts(): Promise<boolean> {
        this.isCommandInProgress = true;
        const command = this.historyService.command(GtthAutoSelectProductsCommand);
        const result = await this.historyService.doCommandBypassHistory(command);
        this.isCommandInProgress = false;
        if (result) {
            this.refreshDesignState();
        }
        return result;
    }

    async designPtmp(): Promise<boolean> {
        if (this.designConfig.p2mpDesignCompleted.every((completed) => !completed)) {
            await this.autoSelectProducts();
        }
        parseFormDataToObj(this.historyService.logicalTopology.autodesignP2mpPerLayer[this.p2mpSelectedLayer], this.editForm);

        this.isCommandInProgress = true;
        const command = this.historyService.command(GtthDesignPtMPCommand).create(this.p2mpSelectedLayer);
        const result = await this.historyService.doCommandBypassHistory(command);
        this.isCommandInProgress = false;
        if (result) {
            this.refreshDesignState();
            this.updateReactiveFormForLayer(this.p2mpSelectedLayer);
        }
        return result;
    }

    async designRings(): Promise<boolean> {
        this.isCommandInProgress = true;
        const command = this.historyService.command(GtthDesignRingsCommand).create(this.ringsSelectedLayer);
        const result = await this.historyService.doCommandBypassHistory(command);
        this.isCommandInProgress = false;
        if (result) {
            this.refreshDesignState();
        }
        return result;
    }

    async autoSelectFrequencies(): Promise<boolean> {
        this.isCommandInProgress = true;
        const command = this.historyService.command(GtthAutoSetFrequenciesCommand);
        const result = await this.historyService.doCommandBypassHistory(command);
        this.isCommandInProgress = false;
        if (result) {
            this.refreshDesignState();
        }
        return result;
    }

    async autoConfigureRings(): Promise<boolean> {
        this.isCommandInProgress = true;
        const command = this.historyService.command(GtthAutoConfigureRingsCommand);
        const result = await this.historyService.doCommandBypassHistory(command);
        this.isCommandInProgress = false;
        if (result) {
            this.refreshDesignState();
        }
        return result;
    }

    async generateBOM(): Promise<void> {
        this.isCommandInProgress = true;
        await this.historyService.bomReportService.clearBom();
        await this.historyService.bomReportService.updateBom(true);
        if (this.historyService.bomReportService.isBomExist()) {
            this.notificationService.showSuccess('BOM successfully generated', 5000);
            const refreshTopologyCommand = this.historyService.command(GtthRefreshTopologyCommand).create(false);
            await this.historyService.doCommandBypassHistory(refreshTopologyCommand);
        }
        this.isCommandInProgress = false;
        return this.refreshDesignState();
    }

    isRestorable(): boolean {
        return true;
    }

    designRequired(): boolean {
        return false;
    }

    nodeCreate(_nodeName: string, _isEnabled: boolean) {
        this.refreshDesignState();
    }

    nodeLayerChanged(_name: string, _newLayerId: number) {
        this.refreshDesignState();
    }

    isCanBeClosed(): boolean {
        return !this.isCommandInProgress;
    }

    public updateReactiveFormForLayer(layerIdx: number): void {
        updateReactiveFormForLayer(this, layerIdx);
        this.initForm(layerIdx);
    }

    private getNodesPerLayerCount(activeLayerTab: number): number {
        const stationsIterator = this.historyService.logicalTopology.getStationsIterator();

        return Array.from(stationsIterator).filter((station) => !station.isPop() && station.getLayerIndex() === activeLayerTab).length;
    }

    private clearRedundancy(layer): void {
        this.historyService.logicalTopology.designRingsPerLayer[layer].rsz_th = 0;
        this.historyService.logicalTopology.designRingsPerLayer[layer].rsz_weig = 0;
    }

    private refreshDataPerLayer() {
        if (this.historyService.logicalTopology.layersModeEnabled) {
            this.historyService.logicalTopology.layers.forEach((_layer: GtthLayerDtoModel, index: number) => {
                this.p2mpDesignDirection[index] = this.historyService.logicalTopology.autodesignP2mpPerLayer[index].bottom_up;
                this.ringsDesignDirection[index] = this.historyService.logicalTopology.designRingsPerLayer[index].bottom_up;
                this.getConnectedLayersFromTopology(index);
            });
        } else {
            this.p2mpDesignDirection = [
                this.historyService.logicalTopology.autodesignP2mpPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX].bottom_up
            ];
            this.ringsDesignDirection = [
                this.historyService.logicalTopology.autodesignP2mpPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX].bottom_up
            ];
            this.getConnectedLayersFromTopology(GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX);
        }
    }

    private createLayersMask() {
        if (this.historyService.logicalTopology.layers?.length) {
            this.historyService.logicalTopology.layers.forEach((layer: GtthLayerDtoModel, index: number) => {
                this.layersMaskObject[layer.getFullName()] = '0x' + Math.pow(2, index);
            });
        }
    }

    private async refreshDesignState(): Promise<void> {
        if (this.isCommandInProgress) {
            return;
        }

        const configCommand = this.historyService.command(GtthGetDesignConfigCommand);
        await this.historyService.doCommandBypassHistory(configCommand);
        this.hasNodesMappedToLayer = this.historyService.logicalTopology.getHasNodesMappedToLayers();
        this.designConfig = configCommand.getDesignConfig();
    }

    private getConnectedLayersFromTopology(layer: number) {
        this.calculateSupportLayers(layer);
        this.calculateFeedByLayers(layer);
    }

    private saveP2mpLayersToTopology(layer: number) {
        this.historyService.logicalTopology.autodesignP2mpPerLayer[layer].feed_by = this.autodesignP2mpLayersPerLayer[layer];
    }

    private saveDesignRingsLayersToTopology(layer: number) {
        this.historyService.logicalTopology.designRingsPerLayer[layer].supp_layer = this.designRingsLayersPerLayer[layer];
    }

    private initReactiveControlsForLayer(layerIdx: number, layerHasMhTG: boolean): void {
        initReactiveControlsForLayer(this, layerIdx, layerHasMhTG);
    }

    private calculateFeedByLayers(layer: number): void {
        if (
            this.historyService.logicalTopology.autodesignP2mpPerLayer[layer] &&
            this.historyService.logicalTopology.autodesignP2mpPerLayer[layer].feed_by > 0
        ) {
            this.autodesignP2mpLayersPerLayer[layer] = this.historyService.logicalTopology.autodesignP2mpPerLayer[layer].feed_by;
            this.autodesignP2mpLayersPerLayerString[layer] = this.getLayersNamesFromBytes(this.autodesignP2mpLayersPerLayer[layer]);
        } else {
            if (this.historyService.logicalTopology.layers?.length) {
                const layerName = this.historyService.logicalTopology.layers[layer].getFullName();
                if (this.layersMaskObject[layerName]) {
                    this.autodesignP2mpLayersPerLayer[layer] = 0 | this.layersMaskObject[layerName];
                    this.autodesignP2mpLayersPerLayerString[layer] = this.getLayersNamesFromBytes(this.autodesignP2mpLayersPerLayer[layer]);
                }
            } else {
                this.autodesignP2mpLayersPerLayer[layer] = Math.pow(2, layer);
            }
            this.saveP2mpLayersToTopology(layer);
        }
    }

    private calculateSupportLayers(layer: number): void {
        if (
            this.historyService.logicalTopology.designRingsPerLayer[layer] &&
            this.historyService.logicalTopology.designRingsPerLayer[layer].supp_layer > 0
        ) {
            this.designRingsLayersPerLayer[layer] = this.historyService.logicalTopology.designRingsPerLayer[layer].supp_layer;
            this.designRingsLayersPerLayerString[layer] = this.getLayersNamesFromBytes(this.designRingsLayersPerLayer[layer]);
        } else {
            if (this.historyService.logicalTopology.layers?.length) {
                const layerName = this.historyService.logicalTopology.layers[layer].getFullName();
                if (this.layersMaskObject[layerName]) {
                    this.designRingsLayersPerLayer[layer] = 0 | this.layersMaskObject[layerName];
                    this.designRingsLayersPerLayerString[layer] = this.getLayersNamesFromBytes(this.designRingsLayersPerLayer[layer]);
                }
            } else {
                this.designRingsLayersPerLayer[layer] = Math.pow(2, layer);
            }
            this.saveDesignRingsLayersToTopology(layer);
        }
    }

    private initForm(layerIdx: number): void {
        this.editForm = this.inputControlService.toFormGroup([
            this.capFacPerLayer[layerIdx],
            this.lcentDistPerLayer[layerIdx],
            this.maxDevNumPerLayer[layerIdx],
            this.limBoxesPerLayer[layerIdx],
            this.redunFacPerLayer[layerIdx],
            this.peerBsFacPerLayer[layerIdx],
            this.takeRatePerLayer[layerIdx],
            this.tunnelFacPerLayer[layerIdx],
            this.asMethodPerLayer[layerIdx],
            this.popDistFacPerLayer[layerIdx],
            this.costFacPerLayer[layerIdx],
            this.nCandWinPerLayer[layerIdx],
            this.tgMaxIttrPerLayer[layerIdx],
            this.p2pDistFacPerLayer[layerIdx],
            this.optBsFacPerLayer[layerIdx]
        ]);
    }
}

@Component({
    selector: 'winde-manual-design-popup',
    template: '',
    standalone: false
})
export class GtthManualDesignPopupComponent implements OnInit {
    constructor(
        private readonly interactiveWindowsPopupService: ProjectInteractiveWindowsPopupService,
        private readonly activatedRoute: ActivatedRoute
    ) {}

    ngOnInit() {
        let bboxParams;
        if (this.activatedRoute.snapshot.queryParamMap.has(PARAM_BBOX)) {
            bboxParams = this.activatedRoute.snapshot.queryParamMap
                .get(PARAM_BBOX)
                .split(',')
                .map((value) => parseFloat(value));
        }
        const routerPath = this.activatedRoute.routeConfig.path;
        this.interactiveWindowsPopupService.openInteractiveWindow(
            GtthManualDesignComponent,
            ProjectInteractiveWindowsPopupService.MANUAL_DESIGN,
            routerPath,
            bboxParams
        );
    }
}
